import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { tabBarSelector } from '../../redux/tabBar/selector';
import { useIntl } from 'react-intl';
import {
  backendErrorCodes,
  couponErrorCodes,
  ErrorCodeType,
  firebaseErrorCodes,
  stripeErrorCodes,
  videoCallingErrorCodes,
} from './types';
import { Toast, ToastProps } from '@mindly/ui-components';
import { useTranslation } from 'react-i18next';
import useIntercomPlugin from 'src/shared/hooks/useIntercomPlugin';

type UsedToastProps = Pick<ToastProps, 'text' | 'positionOffset' | 'position' | 'buttons'>;
type ToastPropsWithDuration = UsedToastProps & {
  durationMilliseconds?: number;
  error?: unknown | string;
  intercomArticleID?: number;
  message?: string;
};
const DEFAULT_DURATION_MS = 8000;

const ToastContext = createContext({
  showToast: (toastProp: ToastPropsWithDuration) => {},
  hideToast: () => {},
});

export const ToastContextProvider: React.FC = ({ children }) => {
  const intl = useIntl();
  const { t } = useTranslation(['gift-certificates']);

  const { displayArticle } = useIntercomPlugin();

  const [activeToast, setActiveToast] = useState<ToastPropsWithDuration | null>(null);
  const { isActive } = useSelector(tabBarSelector);

  const combinedErrorCodesObject = useMemo(
    () => ({
      ...backendErrorCodes(intl),
      ...videoCallingErrorCodes(intl),
      ...firebaseErrorCodes(intl),
      ...stripeErrorCodes(intl),
      ...couponErrorCodes(t),
    }),
    [intl, t]
  );

  const errorNormalizer = useCallback(
    (error: any): ErrorCodeType => {
      const newFormatErrorMessage = Array.isArray(error?.error) ? error?.error?.[0]?.msg : null;

      const errorMessage = combinedErrorCodesObject[
        (error?.decline_code ??
          error?.code ??
          newFormatErrorMessage ??
          error?.error ??
          error?.message ??
          error) as keyof typeof combinedErrorCodesObject
      ] ?? {
        text: intl.formatMessage(
          {
            id: 'app.unknown_error_with_reason',
            defaultMessage: 'Упс, щось пішло не так. Спробуйте ще раз, або зв`яжіться з тех.підтримкою({error}).',
          },
          {
            error:
              newFormatErrorMessage ??
              error?.error ??
              error?.message ??
              error?.code ??
              intl.formatMessage({
                id: 'app.unknown_error',
                defaultMessage: 'Невідома помилка!',
              }),
          }
        ),
      };
      return errorMessage;
    },
    [intl, combinedErrorCodesObject]
  );

  const hideToast = useCallback(() => {
    setActiveToast(null);
  }, [setActiveToast]);

  const openArticle = useCallback((articleID: number) => {
    displayArticle(articleID);
  }, []);

  const defaultPositionOffset = useMemo(() => (isActive ? 73 : 16), [isActive]);

  const showToast = useCallback(
    (toastProp: ToastPropsWithDuration) => {
      let error: ErrorCodeType = {
        text: toastProp.text ?? '',
        intercomArticleID: toastProp?.intercomArticleID,
      };

      if (toastProp?.error) error = errorNormalizer(toastProp.error);

      setActiveToast({
        ...toastProp,
        intercomArticleID: error?.intercomArticleID,
        text: toastProp?.message ?? error?.text,
        buttons: toastProp.buttons?.length
          ? toastProp.buttons
          : [
              {
                text: intl.formatMessage({
                  id: 'general.more_info',
                  defaultMessage: 'Більше інформації',
                }),
                handler: () => {
                  hideToast();
                  return error?.intercomArticleID && openArticle(error?.intercomArticleID);
                },
                hide: !error?.intercomArticleID,
                type: 'outlined',
              },
              {
                text: intl.formatMessage({
                  id: 'general.close',
                  defaultMessage: 'Закрити',
                }),
                handler: hideToast,
                type: 'filled',
              },
            ],
        positionOffset: toastProp?.positionOffset ?? defaultPositionOffset,
      });
    },
    [setActiveToast, errorNormalizer, activeToast]
  );

  useEffect(() => {
    if (activeToast) {
      setTimeout(hideToast, activeToast?.durationMilliseconds ?? DEFAULT_DURATION_MS);
    }
  }, [activeToast]);

  useEffect(() => {
    if (!activeToast) return;

    setActiveToast({
      ...activeToast,
      positionOffset: activeToast?.positionOffset ?? defaultPositionOffset,
    });
  }, [activeToast?.positionOffset, isActive]);

  return (
    <ToastContext.Provider value={{ showToast, hideToast }}>
      {children}
      {activeToast && <Toast {...activeToast} />}
    </ToastContext.Provider>
  );
};

export default function useToast() {
  return useContext(ToastContext);
}
